import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import tastify from '@frontastic/catwalk/src/js/helper/tastify';
import productConnector from '@frontastic/catwalk/src/js/tastic/product/connector';
import _isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import { useCookies } from 'react-cookie';
import { connect, useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import { getChannelIds } from '../../../../helpers/productHelpers';
import useConsentManagement from '../../../../legacy/js/services/hook/useConsentManagement';
import OldTagManager from '../../domain/OldTagManager';
import TagManager from '../../domain/TagManager';
import StoreSidebar from '../../patterns/organisms/OffcanvasStoreFinder';
import ProductDetails from '../../patterns/organisms/ProductDetail/ProductDetails';
import StoreService from '../../services/algolia/StoreService';
import CartService from '../../services/cart';
import GtmService from '../../services/gtm';
import useQueryStore from '../../services/hook/useQueryStore';
import OldGtmService from '../../services/oldGtm';
import PriceService from '../../services/price';
import sortSize from '../../services/sortSize';
import { ConfigurationSchema } from 'frontastic-common';

const getLowestPriceVariantIndex = (variants) => {
  let lowestPriceIndex = -1;
  let lowestPrice = Infinity;
  if (!variants || variants.length === 0) {
    return lowestPriceIndex;
  }

  variants.forEach((variant, index) => {
    if (variant.projectSpecificData.stock >= 1 && variant.price < lowestPrice) {
      lowestPrice = variant.price;
      lowestPriceIndex = index;
    }
  });

  return lowestPriceIndex;
};

const ProductDetailsTastic = ({ product, context, data, node }) => {
  const { isGtmAccepted } = useConsentManagement();

  const apiKey = context.project.configuration.googleMap.mapKey;
  const [availableColorProducts, setAvailableColorProducts] = useState(product?.projectSpecificData?.colors);

  useEffect(() => {
    // Update state when product changes
    setAvailableColorProducts(product?.projectSpecificData?.colors);
  }, [product?.productId]);

  const [cookie] = useCookies(['store']);
  const [isOpenStoreSidebar, setIsOpenStoreSidebar] = useState(false);
  const refererStore = useQueryStore(context);
  const themeName = useSelector((state) => state['theme-status'].themeName);
  const gtmCategoryName = node.name;
  const gtmCategoryId = node.nodeType;
  const router = useRouter();

  const activeProductColor = useMemo(() => {
    if (!availableColorProducts || availableColorProducts.length === 0) {
      return null;
    }

    return availableColorProducts.find((item) => item?.isActive)?.product;
  }, [availableColorProducts]);

  const productBrand = useMemo(() => product?.projectSpecificData?.brand, [product?.projectSpecificData?.brand]);

  useEffect(() => {
    setIsOpenStoreSidebar(false);
  }, [cookie]);

  const skuVariant = router.query.sku;

  const indexVariantFromStock = useMemo(
    () => getLowestPriceVariantIndex(activeProductColor?.variants),
    [activeProductColor?.variants],
  );

  const handleSetLowestPriceVariant = ({ variants, refererStore, isInitialVariant = false }) => {
    if (refererStore) {
      const lowestPriceVariantByStore = PriceService.getVariantHaveLowestPriceProductWithStore(
        variants,
        refererStore.channelId,
      );

      if (lowestPriceVariantByStore) {
        return isInitialVariant ? lowestPriceVariantByStore : handleChangeSelectedVariant(lowestPriceVariantByStore);
      }
    }

    const lowestPriceVariant = PriceService.getVariantHaveLowestPriceProductWithNoStore(variants);
    if (lowestPriceVariant) {
      return isInitialVariant ? lowestPriceVariant : handleChangeSelectedVariant(lowestPriceVariant);
    }
  }

  // Need to set the initial variant to prevent empty body content in the first render
  const [selectedVariant, setSelectedVariant] = useState(!!!skuVariant
    ? handleSetLowestPriceVariant({ variants: activeProductColor?.variants, refererStore, isInitialVariant: true })
    : product?.variants?.find((variant) => variant.sku === skuVariant)
  );

  const handleChangeSelectedVariant = (variant) => {
    const url = new URL(window.location.href);
    const currentRootModelCode = url?.pathname?.match(/\/([^/?]+)(?:\?|$)/)?.[1] || '';
    const savedHistoryStateRootModelCode = (window?.savedHistoryState?.as?.match(/\/([^/?]+)(?:\?|$)/) || [])[1] || '';

    if (currentRootModelCode !== savedHistoryStateRootModelCode || !window.savedHistoryState) {
      // If the root model code has changed, save the current to history state
      window.savedHistoryState = window.history.state;
    }

    if (variant) {
      setSelectedVariant(variant);
      // Set the SKU in the URL if a variant is selected
      url.searchParams.set('sku', variant.sku);
    } else {
      setSelectedVariant(null);
      url.searchParams.delete('sku');
    }
    // Update the URL without reloading the page
    window.history.replaceState(window.savedHistoryState, '', url);
  };

  const projectSchema = useMemo(
    () => new ConfigurationSchema(context.projectConfigurationSchema, context.projectConfiguration),
    [],
  );

  const handleChangeAvailableColorProducts = (newColorProducts) => {
    // newColorProducts is colors that already filtered by store/stock
    // Check if new color products are identical to current ones
    if (_isEqual(newColorProducts, availableColorProducts)) {
      return;
    }


    // Find the active product color
    const newActiveProductColor = newColorProducts.find((item) => item?.isActive)?.product;
    if (!newColorProducts || newColorProducts.length === 0 || !newActiveProductColor) {
      return
    }
    if (newActiveProductColor.productId === activeProductColor.productId) {
      // Case when the active product color hasn't changed -> system filter out variants that are not available
      setAvailableColorProducts(newColorProducts);
      if (skuVariant && selectedVariant?.sku && !newActiveProductColor.variants.find(variant => variant.sku === selectedVariant.sku)) {
        handleSetLowestPriceVariant({ variants: newActiveProductColor.variants, refererStore });
      }

    } else {
      // Create a new URL object based on the current URL
      const url = new URL(window.location.href);
      url.searchParams.delete('sku');
      url.pathname = newActiveProductColor._url;
      // Push the updated URL to the browser's history without reloading the page
      window.history.replaceState({}, '', url);
      // router.push(url, undefined, { shallow: false });
      setAvailableColorProducts(newColorProducts);

      if (selectedVariant?.sku && !newActiveProductColor.variants.find(variant => variant.sku === selectedVariant.sku)) {
        handleSetLowestPriceVariant({ variants: newActiveProductColor.variants, refererStore });
      }
    }
  };

  // Initial set selected variant when product has color
  useEffect(() => {
    if (skuVariant) {
      return;
    }
    handleSetLowestPriceVariant({ variants: activeProductColor?.variants, refererStore });
  }, [activeProductColor?.variants, refererStore, skuVariant]);

  useEffect(() => {
    if (skuVariant) {
      const matchedVariant = activeProductColor?.variants?.find((variant) => variant.sku === skuVariant);
      if (matchedVariant && StoreService.checkIfVariantHaveStockAndPrice({ store: null, variant: matchedVariant })) {
        handleChangeSelectedVariant(matchedVariant);
      } else {
        // // remove sku from url
        handleSetLowestPriceVariant({ variants: activeProductColor?.variants, refererStore: null });
      }
    }
  }, [activeProductColor?.variants, skuVariant]);

  const handleChangeVariantBySku = (sku) => {
    const newActiveVariant = activeProductColor?.variants?.find((variant) => variant.sku === sku);
    if (!newActiveVariant) {
      handleChangeSelectedVariant(activeProductColor?.variants?.[indexVariantFromStock ?? 0]);
      return;
    }
    handleChangeSelectedVariant(newActiveVariant);
  };

  useEffect(() => {
    if (isGtmAccepted && selectedVariant) {
      const productCategories = product?.projectSpecificData?.categories || product?.projectSpecificData?.categories || [];
      const currentCategory = productCategories?.[productCategories.length - 1]
      const gtmService = new GtmService(null, selectedVariant);
      const productGTM = gtmService.createProductGTM('Product Detail', {}, {}, currentCategory);
      TagManager.productDetailImpressions(productGTM);

      // for universal analytics ( will be removed )
      const oldGtmService = new OldGtmService(null, selectedVariant);
      const oldProductGTM = oldGtmService.createProductWithOutPositionGTM();
      OldTagManager.productDetailImpressions(oldProductGTM);
    }
  }, [selectedVariant, isGtmAccepted]);

  // Use effect to filter color products and variants out of stock (by store's channelId or no channel)
  useEffect(() => {
    // Sort color variants before filter
    const projectSpecificDataColors = product?.projectSpecificData?.colors

    const sortedVariantsForProjectSpecificDataColors = projectSpecificDataColors.map((colorProduct) => {
      const sortedVariants = sortSize.sortByProductType(colorProduct.product.variants, projectSchema);
      return {
        ...colorProduct,
        product: {
          ...colorProduct.product,
          variants: sortedVariants,
        },
      };
    })

    // Start to filter color products and variants
    if (!!refererStore && !!sortedVariantsForProjectSpecificDataColors) {
      // filter color products by store's channelId
      const filteredColorProducts = sortedVariantsForProjectSpecificDataColors.filter((colorProduct) => {
        const channelIds = getChannelIds(colorProduct.product);

        if (channelIds.length === 0) {
          return false;
        }

        const isColorProductAvailableInStore = channelIds.includes(refererStore.channelId);
        const isColorProductAvailableInStoreWithStock = colorProduct?.product?.variants?.some((variant) =>
          StoreService.checkIfVariantHaveStockAndPrice({ store: refererStore, variant }),
        );
        return isColorProductAvailableInStore && isColorProductAvailableInStoreWithStock;
      });

      // Filter out variants that are not available
      const colorProductWithFilteredVariants = filteredColorProducts?.map((colorProduct) => {
        const filteredVariants = colorProduct?.product?.variants.filter((variant) =>
          StoreService.checkIfVariantHaveStockAndPrice({ store: refererStore, variant }),
        );
        return {
          ...colorProduct,
          product: {
            ...colorProduct.product,
            variants: filteredVariants,
          },
        };
      });
      if (!colorProductWithFilteredVariants || colorProductWithFilteredVariants.length === 0) {
        // TODO: handle case when no variant available
        router.push('/error/404');
        return
      } else {
        handleChangeAvailableColorProducts(colorProductWithFilteredVariants);
        return
      }

    }
    if (!refererStore && !!sortedVariantsForProjectSpecificDataColors) {
      const colorProductWithFilteredVariants = sortedVariantsForProjectSpecificDataColors?.map((colorProduct) => {
        const filteredVariants = colorProduct?.product?.variants.filter((variant) =>
          StoreService.checkIfVariantHaveStockAndPrice({ store: null, variant }),
        );
        return {
          ...colorProduct,
          product: {
            ...colorProduct.product,
            variants: filteredVariants,
          },
        };
      });
      const colorProductWithFilteredVariantsHaveStock = colorProductWithFilteredVariants.filter(
        (colorProduct) => colorProduct.product.variants.length > 0,
      )
      if (!colorProductWithFilteredVariantsHaveStock || colorProductWithFilteredVariantsHaveStock.length === 0) {
        router.push('/error/404');
        return
      } else {
        handleChangeAvailableColorProducts(colorProductWithFilteredVariantsHaveStock);
        return
      }
    }

  }, [refererStore]);

  const dynamicTitle = (title) => {
    if (!title) {
      return '';
    }

    const productBrand = selectedVariant?.attributes?.att_prod_brand || '';
    return title.replace('[brand]', productBrand);
  };

  const {
    defaultRecommendationProps,
    brandRecommendationProps,
    alignment,
    renderBlackBackground,
    renderDottedBorders,
    renderAsSlider,
    swiperColor,
    headline,
    brandHeadline,
    graphCampaign,
    textColor,
    bgColor,
    positionMode,
    alignText,
    renderDefaultRecommendation,
    renderBrandRecommendation,
    renderStoreRecommendation,
    storeRecommendationProps,
    storeHeadline,
  } = data;
  const nameCategory = selectedVariant?.attributes?.att_prod_brand || '';

  const productRecommendationData = renderDefaultRecommendation
    ? {
      renderBlackBackground,
      renderDottedBorders,
      renderAsSlider,
      swiperColor,
      alignment,
      nameCategory,
      context,
      headline: dynamicTitle(headline),
      recommendationProps: defaultRecommendationProps,
    }
    : null;

  const productBrandRecommendationData = renderBrandRecommendation
    ? {
      renderBlackBackground,
      renderDottedBorders,
      renderAsSlider,
      swiperColor,
      alignment,
      nameCategory,
      context,
      headline: dynamicTitle(brandHeadline),
      recommendationProps: brandRecommendationProps,
    }
    : null;

  const productStoreRecommendationData = !!renderStoreRecommendation
    ? {
      renderBlackBackground,
      renderDottedBorders,
      renderAsSlider,
      swiperColor,
      alignment,
      nameCategory,
      context,
      headline: storeHeadline,
      recommendationProps: storeRecommendationProps,
    }
    : null;

  const { algoliaIndexName } = context?.projectConfiguration || {};

  const toggleOffCanvasStoreFinder = () => {
    if (window?.google?.maps) {
      setIsOpenStoreSidebar(true);
      return;
    }

    StoreService.loadGoogleScript(apiKey).then(() => {
      setIsOpenStoreSidebar(true);
    });
  };

  if (!product) {
    return null;
  }

  return (
    <div>
      <CSSTransition in={isOpenStoreSidebar} mountOnEnter timeout={500} classNames={'off-canvas-store'}>
        <StoreSidebar
          context={context}
          isOpen={isOpenStoreSidebar}
          onClose={() => setIsOpenStoreSidebar(false)}
          selectedVariant={selectedVariant}
          themeName={themeName}
        />
      </CSSTransition>
      <ProductDetails
        key={product.productId}
        productDefault={product}
        productColor={activeProductColor}
        onChangeColorProducts={handleChangeAvailableColorProducts}
        colorProducts={availableColorProducts}
        handleAddToCart={CartService.addToCart}
        onOpenOffCanvasStoreFinder={toggleOffCanvasStoreFinder}
        onChangeVariantBySku={handleChangeVariantBySku}
        selectedVariant={selectedVariant}
        productRecommendationData={productRecommendationData}
        productBrandRecommendationData={productBrandRecommendationData}
        productStoreRecommendationData={productStoreRecommendationData}
        algoliaIndexName={algoliaIndexName}
        graphCampaign={graphCampaign}
        textColor={textColor}
        bgColor={bgColor}
        positionMode={positionMode}
        alignText={alignText}
        refererStore={refererStore}
        gtmCategoryName={gtmCategoryName}
        gtmCategoryId={gtmCategoryId}
        productBrand={productBrand}
        context={context}
      />
    </div>
  );
};

ProductDetailsTastic.propTypes = {
  product: PropTypes.object,
  context: PropTypes.objectOf(PropTypes.any).isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  node: PropTypes.objectOf(PropTypes.any).isRequired,
};

ProductDetailsTastic.defaultProps = {};

export default tastify({
  translate: true,
  connect: {
    tastic: true,
    route: true,
    context: true,
    node: true,
  },
})(connect(productConnector)(ProductDetailsTastic));
